/**
 * pdfDoiDetector — Detecção leve de DOI/ISBN em PDFs
 *
 * Lê apenas as 3 primeiras páginas + metadata dict do PDF.
 * NÃO faz upload, NÃO chama CrossRef, NÃO faz OCR.
 * Objetivo: decidir se PDF vai para Biblioteca ou Desambiguação.
 *
 * Tempo esperado: 1-3s para PDFs de texto.
 * Para PDFs escaneados (sem texto): retorna false → desambiguação.
 */

import { getPdfjsLib } from './pdfjsInit';

// ─────────────────────────────────────────────────────────────────────────────
// Regexes — mesmos padrões do pdfMetadataExtractor.ts (linhas 47-69)
// Mantidos aqui separados para não tocar no extractor existente (Pilar 3).
// ─────────────────────────────────────────────────────────────────────────────

const DOI_PATTERN = /10\.\d{4,}(?:\.\d+)*\/[^\s]+/g;

const ISBN_13_PATTERN = /(?:ISBN(?:-13)?:?\s*)?(?:978|979)[\s-]?\d{1,5}[\s-]?\d{1,7}[\s-]?\d{1,7}[\s-]?\d/gi;
const ISBN_10_PATTERN = /(?:ISBN(?:-10)?:?\s*)?\d{1,5}[\s-]?\d{1,7}[\s-]?\d{1,7}[\s-]?[\dXx]/gi;

function extractDoi(text: string): string | null {
  const matches = text.match(DOI_PATTERN);
  if (matches && matches.length > 0) {
    return matches[0].replace(/[.,;:\])\}]+$/, '');
  }
  return null;
}

function extractIsbn(text: string): string | null {
  let matches = text.match(ISBN_13_PATTERN);
  if (matches && matches.length > 0) {
    return matches[0].replace(/[^\d]/g, '');
  }
  matches = text.match(ISBN_10_PATTERN);
  if (matches && matches.length > 0) {
    return matches[0].replace(/[^\dXx]/g, '');
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Resultado
// ─────────────────────────────────────────────────────────────────────────────

export interface DoiDetectionResult {
  hasDoi: boolean;
  doi: string | null;
  hasIsbn: boolean;
  isbn: string | null;
  /**
   * Texto extraído das primeiras 3 páginas do PDF (concatenado).
   * Disponibilizado para callers que precisam aplicar heurísticas
   * adicionais sem re-extrair o texto (ex: pdfKindHeuristic).
   * Vazio quando a extração falha.
   */
  extractedText: string;
}

const EMPTY_RESULT: DoiDetectionResult = {
  hasDoi: false,
  doi: null,
  hasIsbn: false,
  isbn: null,
  extractedText: '',
};

// ─────────────────────────────────────────────────────────────────────────────
// Detector principal
// ─────────────────────────────────────────────────────────────────────────────

const MAX_PAGES = 3;

/**
 * Tempo máximo total para a detecção. PDFs grandes (votos STF, prontuários
 * digitalizados, livros escaneados) podem travar pdfjs por 30s+ ou indefinidamente.
 * Após este timeout, retornamos EMPTY_RESULT e o pipeline cai diretamente na
 * desambiguação manual — usuário escolhe Biblioteca/Repositório sem sugestão
 * da heurística (que depende do extractedText).
 */
const DETECTION_TIMEOUT_MS = 8000;

/**
 * Extração LEVE de DOI/ISBN — lê apenas as 3 primeiras páginas.
 * Fallback seguro: se falhar OU exceder DETECTION_TIMEOUT_MS, retorna
 * EMPTY_RESULT → desambiguação manual.
 *
 * Hotfix Tarefa 7: PDFs grandes (>50MB ou >100 páginas com fontes embutidas)
 * podem travar pdfjs.getPage().getTextContent() indefinidamente. Sem timeout,
 * o pipeline inteiro fica pendurado. Promise.race garante retorno em ≤8s.
 */
export async function detectDoiLightweight(file: File): Promise<DoiDetectionResult> {
  const timeoutPromise = new Promise<DoiDetectionResult>((resolve) => {
    setTimeout(() => resolve(EMPTY_RESULT), DETECTION_TIMEOUT_MS);
  });

  try {
    return await Promise.race([detectDoiLightweightInternal(file), timeoutPromise]);
  } catch {
    return EMPTY_RESULT;
  }
}

async function detectDoiLightweightInternal(file: File): Promise<DoiDetectionResult> {
  try {
    const pdfjsLib = getPdfjsLib();
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    // Coletar texto das primeiras páginas
    const pagesToRead = Math.min(pdf.numPages, MAX_PAGES);
    let text = '';

    for (let i = 1; i <= pagesToRead; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      text += content.items
        .map((item) => {
          const textItem = item as { str?: string };
          return textItem.str || '';
        })
        .join(' ') + '\n';
    }

    // Verificar metadata dict do PDF
    try {
      const metadata = await pdf.getMetadata();
      const info = metadata?.info as Record<string, string> | undefined;
      if (info) {
        const metaFields = [info.Subject, info.Keywords, info.doi, info.Description]
          .filter(Boolean)
          .join(' ');
        text = metaFields + '\n' + text;
      }
    } catch {
      // Metadata não disponível — continuar sem ela
    }

    const doi = extractDoi(text);
    const isbn = extractIsbn(text);

    return {
      hasDoi: !!doi,
      doi,
      hasIsbn: !!isbn,
      isbn,
      extractedText: text,
    };
  } catch {
    // Se não conseguiu ler o PDF, vai para desambiguação
    return EMPTY_RESULT;
  }
}
